import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { ApiStatus } from "../../utils/api.types";
import {
  fetchRequisitions,
  fetchRequisitionById,
  addRequisition,
  updateRequisition,
  submitRequisition,
  approveRequisition,
  rejectRequisition,
  createTransferFromRequisition,
  reopenRequisition,
} from "./requisitionThunk";

interface ReqSliceState {
  requisitions: any[];
  requisitionDetails: any;
  rowCount: number;
  status: ApiStatus;
  errorMessage?: string;
}

const initialState: ReqSliceState = { requisitions: [], requisitionDetails: {}, rowCount: 0, status: ApiStatus.IDLE, errorMessage: "" };

const requisitionSlice = createSlice({
  name: "requisition",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchRequisitions.pending, (state) => { state.status = ApiStatus.LOADING; })
      .addCase(fetchRequisitions.fulfilled, (state, action: any) => {
        state.status = ApiStatus.SUCCESS;
        // Server-side paginated: { data, total }. Falls back to a plain array for safety.
        const p = action.payload;
        state.requisitions = Array.isArray(p) ? p : p?.data || [];
        state.rowCount = Array.isArray(p) ? p.length : p?.total || 0;
      })
      .addCase(fetchRequisitions.rejected, (state, action) => { state.status = ApiStatus.FAILED; state.errorMessage = action.error.message; })

      .addCase(fetchRequisitionById.fulfilled, (state, action: any) => { state.requisitionDetails = action.payload || {}; })

      .addCase(addRequisition.fulfilled, () => { toast.success("Requisition created"); })
      .addCase(addRequisition.rejected, (state, action) => { toast.error(action.error.message || "Failed to create requisition"); })
      .addCase(updateRequisition.fulfilled, () => { toast.success("Requisition updated"); })
      .addCase(updateRequisition.rejected, (state, action) => { toast.error(action.error.message || "Failed to update"); })
      .addCase(submitRequisition.fulfilled, () => { toast.success("Requisition submitted"); })
      .addCase(submitRequisition.rejected, (state, action) => { toast.error(action.error.message || "Failed to submit"); })
      .addCase(approveRequisition.fulfilled, () => { toast.success("Requisition approved"); })
      .addCase(approveRequisition.rejected, (state, action) => { toast.error(action.error.message || "Failed to approve"); })
      .addCase(rejectRequisition.fulfilled, () => { toast.success("Requisition rejected"); })
      .addCase(rejectRequisition.rejected, (state, action) => { toast.error(action.error.message || "Failed to reject"); })
      .addCase(createTransferFromRequisition.fulfilled, () => { toast.success("Transfer created from requisition"); })
      .addCase(createTransferFromRequisition.rejected, (state, action) => { toast.error(action.error.message || "Failed to create transfer"); })
      .addCase(reopenRequisition.fulfilled, () => { toast.success("Requisition reopened (reset to draft)"); })
      .addCase(reopenRequisition.rejected, (state, action) => { toast.error(action.error.message || "Failed to reopen"); });
  },
});

export default requisitionSlice.reducer;
